import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import emptyBackgroundUrl from '@/assets/campaigns/landing/empty-bg.svg'
import emptyFloorUrl from '@/assets/campaigns/landing/empty-floor.svg'
import emptyIconsUrl from '@/assets/campaigns/landing/empty-icons.svg'
import emptyMegaphoneUrl from '@/assets/campaigns/landing/empty-megaphone.svg'
import emptyQuestionAnswerUrl from '@/assets/campaigns/landing/empty-question-answer.svg'
import emptyStarsUrl from '@/assets/campaigns/landing/empty-stars.svg'
import type { CampaignLandingFilter } from '../lib/campaign-landing-display'

type Props = Readonly<{
  isAdmin: boolean
  statusFilter: CampaignLandingFilter
  searchQuery?: string
}>

/** Figma `5110:8565` — 282×282 illustration frame. */
const EMPTY_ILLUSTRATION_SIZE_PX = 282

const ILLUSTRATION_LAYERS = [
  { src: emptyBackgroundUrl, inset: '4.78% 6.47% 16.27% 9.6%' },
  { src: emptyFloorUrl, inset: '93.81% 5.91% 6.08% 5.5%' },
  { src: emptyIconsUrl, inset: '21.2% 5.6% 18.28% 9.65%' },
  { src: emptyMegaphoneUrl, inset: '24.47% 27.81% 8.96% 21.99%' },
  { src: emptyStarsUrl, inset: '9.97% 11.2% 33.34% 9.74%' },
] as const

type Inset = Readonly<{ top: string; right: string; bottom: string; left: string }>

function parseInset(inset: string): Inset {
  const [top, right, bottom, left] = inset.split(/\s+/)
  return { top, right, bottom, left }
}

function EmptyIllustrationLayer({
  src,
  inset,
}: Readonly<{ src: string; inset: string }>) {
  const { top, right, bottom, left } = parseInset(inset)

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top,
        right,
        bottom,
        left,
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          width: '100%',
          height: '100%',
          maxWidth: 'none',
        }}
      />
    </Box>
  )
}

function EmptyQuestionAnswerLayer() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: '25.18%',
        right: '20.21%',
        bottom: '66.31%',
        left: '71.28%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '8.33%',
          right: '8.33%',
          bottom: '6.25%',
          left: '4.17%',
        }}
      >
        <Box
          component="img"
          src={emptyQuestionAnswerUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'block',
            width: '100%',
            height: '100%',
            maxWidth: 'none',
          }}
        />
      </Box>
    </Box>
  )
}

function emptyMessage(
  isAdmin: boolean,
  statusFilter: CampaignLandingFilter,
  searchQuery: string,
): string {
  if (searchQuery.trim().length > 0) {
    return 'No campaigns found matching your search.'
  }
  if (statusFilter !== 'all') {
    return 'No campaigns found with this status.'
  }
  if (isAdmin) {
    return 'No campaigns found. Create your first campaign to get started.'
  }
  return 'No campaigns assigned yet.'
}

export function CampaignListEmptyState({
  isAdmin,
  statusFilter,
  searchQuery = '',
}: Props) {
  const showIllustration =
    statusFilter === 'all' && searchQuery.trim().length === 0

  return (
    <Box
      data-testid="campaign-list-empty-state"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: showIllustration ? { xs: 4, md: '72px' } : { xs: 8, md: '120px' },
        pb: 6,
      }}
    >
      {showIllustration ? (
        <Box
          aria-hidden
          data-testid="campaign-list-empty-illustration"
          sx={{
            position: 'relative',
            width: EMPTY_ILLUSTRATION_SIZE_PX,
            height: EMPTY_ILLUSTRATION_SIZE_PX,
            mb: '10px',
            flexShrink: 0,
          }}
        >
          {ILLUSTRATION_LAYERS.map((layer) => (
            <EmptyIllustrationLayer key={layer.src} src={layer.src} inset={layer.inset} />
          ))}
          <EmptyQuestionAnswerLayer />
        </Box>
      ) : null}

      <Typography
        data-testid="campaign-list-empty-message"
        sx={{
          fontSize: '14px',
          lineHeight: '20px',
          color: '#64748b',
          textAlign: 'center',
          maxWidth: 394,
        }}
      >
        {emptyMessage(isAdmin, statusFilter, searchQuery)}
      </Typography>
    </Box>
  )
}
